// PORTF Excel export (Drummond users only) for the Portfolio Lab Compare view.
//
// Produces a single-sheet datafile for upload to Refinitiv PORT: the CURRENT
// holdings of every compared portfolio, replicated across every quarter-end date
// from 30 Sep 2019 to the most recent COMPLETED quarter end.
//
// Columns: Portfolio_Name | Portfolio_Code | Fund_Code | Weight | Date
//   • Fund_Code is the Refinitiv identifier already carried on each holding
//     (Shadow TR_Code — Lipper LPxxxx / RIC). Cash-asset-class holdings are
//     remapped to '.ONEAUD', matching Portfolio_Database.py's Eikon upload
//     (np.where(Sub_Asset_Class == 'Cash', '.ONEAUD', TR_Code)).
//   • Weight is the on-screen holdings weight (already in percentage points),
//     passed through unchanged.
import XLSX from 'xlsx-js-style';

const HDR = {
  font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
  fill: { fgColor: { rgb: '072040' } },
  alignment: { horizontal: 'center' },
};

// Refinitiv portfolio code, where the compare key exposes one. Model and SAA
// keys embed their code; custom (user-built) portfolios have none, so blank.
function portfolioCode(key) {
  if (key.startsWith('model-')) return key.slice('model-'.length);
  if (key.startsWith('saa-')) return key.slice('saa-'.length);
  return '';
}

// A local-midnight date shifted so its Excel serial lands on the same calendar
// day regardless of the browser timezone. Excel date serials are timezone-naive;
// without this, a positive-UTC-offset user (e.g. AEST) writes the day before.
function utcNaive(y, mo, day) {
  const d = new Date(y, mo, day);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000);
}

// Quarter-end dates from 30 Sep 2019 to the most recent COMPLETED quarter end.
// [monthIndex, day] for Mar / Jun / Sep / Dec quarter ends.
function quarterEndDates() {
  const qdef = [[2, 31], [5, 30], [8, 30], [11, 31]];
  const today = new Date();
  // Day 0 of the current quarter's start month = last day of the previous
  // quarter = the most recent completed quarter end.
  const cutoff = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 0);
  const dates = [];
  for (let y = 2019; y <= cutoff.getFullYear(); y += 1) {
    for (let q = 0; q < 4; q += 1) {
      if (y === 2019 && q < 2) continue; // start at Sep 2019
      if (new Date(y, qdef[q][0], qdef[q][1]) <= cutoff) {
        dates.push(utcNaive(y, qdef[q][0], qdef[q][1]));
      }
    }
  }
  return dates;
}

const isCash = (ac) => String(ac || '').trim().toLowerCase() === 'cash';
const w4 = (v) => (v == null || !Number.isFinite(+v) ? '' : +(+v).toFixed(4));

const HEADER = ['Portfolio_Name', 'Portfolio_Code', 'Fund_Code', 'Weight', 'Date'];

// Build a styled worksheet from an array-of-arrays whose last column holds Date
// objects. Row 0 gets the navy header; the Date column is forced to real date
// cells (independent of aoa_to_sheet's Date inference) so the file uploads with
// proper dates, not text.
function buildSheet(aoa) {
  const ws = XLSX.utils.aoa_to_sheet(aoa, { cellDates: true });
  ws['!cols'] = [{ wch: 34 }, { wch: 16 }, { wch: 16 }, { wch: 12 }, { wch: 12 }];
  for (let c = 0; c < HEADER.length; c += 1) {
    const ref = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[ref]) ws[ref].s = HDR;
  }
  for (let r = 1; r < aoa.length; r += 1) {
    const d = aoa[r][4];
    if (d instanceof Date) {
      ws[XLSX.utils.encode_cell({ r, c: 4 })] = { t: 'd', v: d, z: 'yyyy-mm-dd' };
    }
  }
  return ws;
}

// Excel sheet names: <=31 chars, unique, no : \ / ? * [ ].
function appendSheet(wb, used, name, ws) {
  const base = String(name).replace(/[:\\/?*[\]]/g, ' ').slice(0, 31).trim() || 'Sheet';
  let n = base;
  let i = 2;
  while (used.has(n)) { const s = ` ${i}`; n = base.slice(0, 31 - s.length) + s; i += 1; }
  used.add(n);
  XLSX.utils.book_append_sheet(wb, ws, n);
}

export function exportPortfToExcel(payload) {
  const { comparisonTable } = payload;
  const ports = comparisonTable?.portfolios || [];
  const rows = comparisonTable?.rows || [];
  if (ports.length === 0 || rows.length === 0) return false;

  const dates = quarterEndDates();
  if (dates.length === 0) return false;

  const wb = XLSX.utils.book_new();
  const used = new Set();

  // One sheet per portfolio: its held funds replicated across every date.
  ports.forEach((p) => {
    const code = portfolioCode(p.key);
    const held = rows
      .filter((row) => row.weights?.[p.key] != null && row.weights[p.key] !== 0)
      .map((row) => ({
        name: p.name,
        code,
        fund: isCash(row.assetClass) ? '.ONEAUD' : (row.code || ''),
        weight: w4(row.weights[p.key]),
      }));
    if (held.length === 0) return; // nothing held — skip empty sheet
    const aoa = [HEADER];
    dates.forEach((d) => {
      held.forEach((h) => aoa.push([h.name, h.code, h.fund, h.weight, d]));
    });
    // Prefer the portfolio code as the sheet name (short, unique, Refinitiv-
    // friendly); fall back to the name for custom portfolios with no code.
    appendSheet(wb, used, code || p.name, buildSheet(aoa));
  });

  if (wb.SheetNames.length === 0) return false;

  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `PORTF_Upload_${stamp}.xlsx`);
  return true;
}
